import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from '../utils/apiClient';
import { useAuthStore } from './authStore';

export const useOptInStore = create(
  persist(
    (set, get) => ({
      // Generic opt-ins (quick_users_guide, ...) keyed `email|name`.
      optIns: {},

      // License agreement that applies to the signed-in user, as served by
      // POST /license-agreement-request {type: "current"}: which agreement
      // (per-company, by email domain, else the default) and whether they've
      // accepted it. Persisted so LicenseGuard can gate synchronously.
      license: null, // { email, agreement, accepted, accepted_at, company }

      hasAcceptedLicense: () => {
        const { license } = get();
        const user = useAuthStore.getState().user;
        const userEmail = user?.email || user?.id || '';
        if (!license || !userEmail) return false;
        return license.email === userEmail && Boolean(license.accepted);
      },

      // Fetch the current agreement + acceptance state from the server.
      // Returns the status object, or null on failure.
      fetchLicenseStatus: async (email) => {
        const user = useAuthStore.getState().user;
        const userEmail = email || user?.email || user?.id || '';
        try {
          const res = await apiClient.post('/license-agreement-request', { type: 'current' });
          const current = res?.records?.[0];
          if (!current) return null;
          const license = { email: userEmail, ...current };
          set({ license });
          return license;
        } catch (error) {
          console.error('Error fetching license agreement status:', error);
          return null;
        }
      },

      // Generic: fetch any named opt-in's status from the server (license,
      // quick_users_guide, etc. — same `email|name` id shape). Falls back to
      // the auth store's user when no email is passed.
      fetchOptIn: async (name, email) => {
        let userEmail = email;
        if (!userEmail) {
          const user = useAuthStore.getState().user;
          userEmail = user?.email || user?.id || '';
        }
        if (!userEmail || typeof userEmail !== 'string' || !userEmail.trim()) {
          console.error(`No email provided for ${name} opt-in check`);
          return false;
        }

        try {
          const optInId = `${userEmail}|${name}`;
          const response = await apiClient.get(`/opt-in/${optInId}`);

          if (response && response.value === "true") {
            localStorage.setItem('userEmail', userEmail);
            set(state => ({
              optIns: {
                ...state.optIns,
                [optInId]: {
                  accepted: true,
                  timestamp: response.created || Date.now()
                }
              }
            }));
            return true;
          }
          return false;
        } catch (error) {
          // If 404 or any error, it means the user hasn't opted in
          console.log(`User has not previously opted in to ${name}`);
          return false;
        }
      },

      // Generic: record any named opt-in for the user.
      setOptIn: async (name, email) => {
        if (!email || typeof email !== 'string' || !email.trim()) {
          console.error(`No email provided for ${name} opt-in`);
          return false;
        }

        try {
          const timestamp = Date.now();
          await apiClient.post("/opt-in", {
            id: `${email}|${name}`,
            email,
            name,
            value: "true",
            created: timestamp
          });

          localStorage.setItem('userEmail', email);
          set(state => ({
            optIns: {
              ...state.optIns,
              [`${email}|${name}`]: {
                accepted: true,
                timestamp
              }
            }
          }));
          return true;
        } catch (error) {
          console.error(`Error recording ${name} opt-in:`, error);
          return false;
        }
      },

      // Login/SSO callback: has the user accepted the agreement that applies to them?
      fetchLicenseOptIn: async (email) => Boolean((await get().fetchLicenseStatus(email))?.accepted),

      // Record acceptance of `agreementId` (must be the one fetchLicenseStatus
      // returned — the server re-resolves and 409s on a mismatch).
      acceptLicense: async (agreementId) => {
        await apiClient.post('/license-agreement-request', { type: 'accept', agreement_id: agreementId });
        set((state) => ({ license: { ...state.license, accepted: true, accepted_at: Date.now() } }));
        return true;
      },

      // Check if specific opt-in exists
      checkOptIn: (email, name) => {
        if (!email || typeof email !== 'string' || !email.trim()) return false;

        const state = get();
        return Boolean(state.optIns[`${email}|${name}`]?.accepted);
      },

      // Reset all opt-ins (e.g., on logout)
      resetOptIns: () => {
        localStorage.removeItem('userEmail');
        set({ optIns: {}, license: null });
      }
    }),
    {
      name: 'opt-in-storage', // Name for localStorage
    }
  )
);